import { useState, useEffect, useRef } from "react";
import { translate } from "../../localization/translateService";

// Constants
const MOBILE_BREAKPOINT = 1120;
// Logo navigates back to the site home (localized root, e.g. /en, /vi, /ko)

// GNB menu — exactly 4 items per the "GNB" tab of the i18n spreadsheet.
// `href` is appended to the language path (e.g. /ko + href). All flat (no dropdowns).
// (Other items — VOD, Dotore shorts, Support, News, Info Board — hidden per GNB spec.)
const NAV_CONFIG = [
  { key: "liveClass", labelKey: "UMBRACO_LANDING.HEADER.LIVE_CLASS_NEW", fallback: "Live Lessons", href: "/courses?page=1&rows=12&sortBy=newest" },
  { key: "lecture", labelKey: "UMBRACO_LANDING.HEADER.1_1_LECTURE", fallback: "1:1 Lecture", href: "/real-time-lectures" },
  { key: "levelTest", labelKey: "UMBRACO_LANDING.HEADER.LEVEL_TEST_NEW", fallback: "Find My Level", href: "/pretest" },
  { key: "events", labelKey: "UMBRACO_LANDING.HEADER.EVENTS_NEW", fallback: "Promotions", href: "/events" },
  { key: "customerCenter", labelKey: "UMBRACO_LANDING.HEADER.CUSTOMER_CENTER_NEW", fallback: "Support", href: "/contact-us?tab=CUSTOMER_CENTER" },
];

export default function MainNavigation({ language, onOpenPopup, rightSlot, scheme = "light" }) {
  const logo = scheme === "dark"
    ? require("../../icons/dotore-logo-white.svg")
    : require("../../icons/dotore-logo.svg");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openKey, setOpenKey] = useState(null); // mobile-only expanded dropdown
  const [labels, setLabels] = useState({});
  const navRef = useRef(null);

  // Load (translated) labels with English fallbacks
  useEffect(() => {
    const load = async () => {
      try {
        const out = {};
        for (const item of NAV_CONFIG) {
          out[item.labelKey] = await translate(item.labelKey, false, item.fallback);
          for (const c of item.children || []) {
            out[c.labelKey] = await translate(c.labelKey, false, c.fallback);
          }
        }
        setLabels(out);
      } catch {
        const out = {};
        NAV_CONFIG.forEach((i) => {
          out[i.labelKey] = i.fallback;
          (i.children || []).forEach((c) => (out[c.labelKey] = c.fallback));
        });
        setLabels(out);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > MOBILE_BREAKPOINT) setMobileMenuOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => { setMobileMenuOpen(false); setOpenKey(null); };
  const toggleMobileMenu = () => setMobileMenuOpen((v) => !v);

  const items = NAV_CONFIG.map((i) => ({
    ...i,
    label: labels[i.labelKey] || i.fallback,
    fullHref: i.href ? `${language}${i.href}` : "",
    children: (i.children || []).map((c) => ({
      ...c,
      label: labels[c.labelKey] || c.fallback,
      fullHref: `${language}${c.href}`,
    })),
  }));

  const isActive = (item) =>
    item.href && window.location.pathname.includes(item.href.split("?")[0]);

  // Top-level link (or non-navigating label for empty href)
  const renderTopLink = (item) => {
    if (!item.fullHref) {
      return (
        <a className="nav-a is-disabled" role="link" aria-disabled="true" onClick={(e) => e.preventDefault()}>
          {item.label}
        </a>
      );
    }
    return (
      <a href={item.fullHref} className={`nav-a ${isActive(item) ? "active" : ""}`} onClick={closeMobileMenu}>
        {item.label}
      </a>
    );
  };

  return (
    <>
      <nav className="nav-container" ref={navRef}>
        <div className="nav-wrapper">
          <div className="nav-bar">
            {/* CI logo -> dotokorean.com */}
            <a href={language || "/"} className="no-underline">
              <img src={logo} alt="DOTORE" className="header-logo m-0 cursor-pointer" />
            </a>

            {/* Desktop menu */}
            <ul className="nav-menu">
              {items.map((item) => (
                <li key={item.key} className={`nav-item ${item.children.length ? "has-dropdown" : ""}`}>
                  <span className="nav-link-wrap">
                    {renderTopLink(item)}
                    {item.children.length > 0 && <i className="pi pi-angle-down nav-caret" aria-hidden="true" />}
                  </span>
                  {item.children.length > 0 && (
                    <ul className="nav-submenu">
                      {item.children.map((c, idx) => (
                        <li key={idx} className="nav-subitem">
                          <a href={c.fullHref} className="nav-sub-a" onClick={closeMobileMenu}>{c.label}</a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>

            {/* Right-side actions (merged from the former orange top bar) */}
            {rightSlot ? <div className="nav-actions">{rightSlot}</div> : null}

            {/* Mobile toggle */}
            <button className="mobile-toggle" onClick={toggleMobileMenu} aria-label="Toggle mobile menu">
              <i className={`pi ${mobileMenuOpen ? "pi-times" : "pi-bars"}`} />
            </button>
          </div>

          {/* Mobile menu */}
          <div className={`mobile-menu ${mobileMenuOpen ? "show" : ""}`}>
            <ul className="mobile-nav-list">
              {items.map((item) => (
                <li key={item.key} className="mobile-nav-item">
                  {item.children.length > 0 ? (
                    <>
                      <button
                        className="mobile-nav-a mobile-nav-parent"
                        onClick={() => setOpenKey(openKey === item.key ? null : item.key)}
                      >
                        {item.label}
                        <i className={`pi ${openKey === item.key ? "pi-angle-up" : "pi-angle-down"}`} />
                      </button>
                      {openKey === item.key && (
                        <ul className="mobile-subnav">
                          {item.children.map((c, idx) => (
                            <li key={idx}>
                              <a href={c.fullHref} className="mobile-nav-a mobile-sub-a" onClick={closeMobileMenu}>{c.label}</a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : item.fullHref ? (
                    <a href={item.fullHref} className="mobile-nav-a" onClick={closeMobileMenu}>{item.label}</a>
                  ) : (
                    <a className="mobile-nav-a is-disabled" onClick={(e) => e.preventDefault()}>{item.label}</a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </nav>

      <div className={`mobile-overlay ${mobileMenuOpen ? "show" : ""}`} onClick={closeMobileMenu} />
    </>
  );
}
